import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  FaPlus, 
  FaEdit, 
  FaTrash, 
  FaFilter,
  FaHandshake,
  FaProjectDiagram
} from 'react-icons/fa';
import CompactActionBar from '../components/CompactActionBar';
import { partnerService, Partner, CreatePartnerData } from '../services/partnerService';
import { projectService } from '../services/projectService';
import ConfirmDialog from '../components/ConfirmDialog';
import { PermissionGuard } from '../components/PermissionGuard';
import '../css/pages/Partners.css';

interface Project {
  id: number;
  name: string;
}

const Partners: React.FC = () => {
  const navigate = useNavigate();
  const [partners, setPartners] = useState<Partner[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [editingPartner, setEditingPartner] = useState<Partner | null>(null);
  const [assigningPartner, setAssigningPartner] = useState<Partner | null>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [partnerToDelete, setPartnerToDelete] = useState<Partner | null>(null);
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [limit, setLimit] = useState(100);
  
  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [projectFilter, setProjectFilter] = useState('');
  
  // Form data
  const [formData, setFormData] = useState({
    name: '',
    projectId: '',
    percentage: ''
  });

  // Assignment form data
  const [assignData, setAssignData] = useState({
    projectId: '',
    percentage: ''
  });

  // Debounced search effect to prevent input focus loss
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchPartners();
    }, searchTerm ? 500 : 0);

    return () => clearTimeout(timeoutId);
  }, [currentPage, searchTerm, projectFilter]);

  // Initial data load
  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchPartners = async () => {
    try {
      setLoading(true);
      const params = {
        page: currentPage,
        limit,
        ...(searchTerm && { search: searchTerm })
      };
      
      const response = await partnerService.getPartners(params);
      let filteredPartners = response.partners;
      
      // Filter by project if selected
      if (projectFilter) {
        filteredPartners = response.partners.filter(partner => 
          partner.projects?.some(pp => pp.project.id === parseInt(projectFilter))
        );
      }
      
      setPartners(filteredPartners);
      setTotal(response.total);
      setTotalPages(response.totalPages);
    } catch (error) {
      console.error('Error fetching partners:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchProjects = async () => {
    try {
      const projects = await projectService.getProjects(true); // Get only active projects
      setProjects(projects);
      
      // Auto-select first project
      if (projects.length > 0) {
        setProjectFilter(projects[0].id.toString());
      }
    } catch (error) {
      console.error('Error fetching projects:', error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      if (editingPartner) {
        // Update partner name
        await partnerService.updatePartner(editingPartner.id, { name: formData.name });
        
        // Handle project assignment for editing
        if (editingPartner.projects && editingPartner.projects.length > 0) {
          // Remove existing project assignments
          for (const existingAssignment of editingPartner.projects) {
            await partnerService.removePartnerFromProject(existingAssignment.id);
          }
        }
        
        // Add new project assignment if selected
        if (formData.projectId && formData.percentage) {
          await partnerService.assignPartnerToProject({
            partnerId: editingPartner.id,
            projectId: parseInt(formData.projectId),
            percentage: parseFloat(formData.percentage)
          });
        }
      } else {
        // For new partner, create partner and assign to project
        const newPartner = await partnerService.createPartner({ name: formData.name });
        
        if (formData.projectId && formData.percentage) {
          await partnerService.assignPartnerToProject({
            partnerId: newPartner.id,
            projectId: parseInt(formData.projectId),
            percentage: parseFloat(formData.percentage)
          });
        }
      }
      
      setShowModal(false);
      setEditingPartner(null);
      resetForm();
      fetchPartners();
    } catch (error) {
      console.error('Error saving partner:', error);
    }
  };

  const handleEdit = (partner: Partner) => {
    navigate(`/partner/edit?id=${partner.id}`);
  };

  const handleView = (partner: Partner) => {
    navigate(`/partner/view?id=${partner.id}`);
  };

  const handleDelete = (partner: Partner) => {
    setPartnerToDelete(partner);
    setShowDeleteDialog(true);
  };

  const confirmDelete = async () => {
    if (partnerToDelete) {
      try {
        await partnerService.deletePartner(partnerToDelete.id);
        fetchPartners();
      } catch (error) {
        console.error('Error deleting partner:', error);
      }
    }
    setShowDeleteDialog(false);
    setPartnerToDelete(null);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      projectId: '',
      percentage: ''
    });
    setAssignData({
      projectId: '',
      percentage: ''
    });
  };

  const handleAssignProject = (partner: Partner) => {
    setAssigningPartner(partner);
    setShowAssignModal(true);
  };

  const handleAssignSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!assigningPartner || !assignData.projectId || !assignData.percentage) {
      alert('Please select a project and enter percentage');
      return;
    }

    try {
      await partnerService.assignPartnerToProject({
        partnerId: assigningPartner.id,
        projectId: parseInt(assignData.projectId),
        percentage: parseFloat(assignData.percentage)
      });
      
      setShowAssignModal(false);
      setAssigningPartner(null);
      resetForm();
      fetchPartners();
    } catch (error) {
      console.error('Error assigning partner to project:', error);
      alert('Failed to assign partner to project');
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleProjectFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setProjectFilter(e.target.value);
    setCurrentPage(1);
  };

  const getPartnerProjectsDisplay = (partner: Partner) => {
    if (!partner.projects || partner.projects.length === 0) {
      return 'No projects assigned';
    }
    
    return partner.projects
      .map(pp => `${pp.project.name} (${pp.percentage}%)`)
      .join(', ');
  };

  if (loading && partners.length === 0) {
    return <div className="loading">Loading partners...</div>;
  }

  return (
    <div className="page-container">
      {/* Action Bar */}
      <CompactActionBar>
        <div style={{ display: "flex", gap: "12px", alignItems: "center", flex: 1 }}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <span style={{ fontSize: "13px", color: "#6b7280" }}>Project:</span>
            <select
              value={projectFilter}
              onChange={handleProjectFilterChange}
              style={{
                padding: "6px 10px",
                border: "1px solid #e5e7eb",
                borderRadius: "6px",
                fontSize: "13px",
              }}
            >
              <option value="">All Projects</option>
              {projects.map(project => (
                <option key={project.id} value={project.id}>{project.name}</option>
              ))}
            </select>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <span style={{ fontSize: "13px", color: "#6b7280" }}>Show:</span>
            <select
              value={limit}
              onChange={(e) => {
                setLimit(parseInt(e.target.value));
                setCurrentPage(1);
              }}
              style={{
                padding: "6px 10px",
                border: "1px solid #e5e7eb",
                borderRadius: "6px",
                fontSize: "13px",
              }}
            >
              <option value={100}>100</option>
              <option value={200}>200</option>
              <option value={300}>300</option>
              <option value={400}>400</option>
              <option value={500}>500</option>
            </select>
          </div>

          <PermissionGuard permission="partner.create">
            <button 
              className="btn btn-primary"
              onClick={() => navigate('/partner/add')}
              style={{ marginLeft: 'auto', padding: '6px 12px', fontSize: '13px' }}
            >
              <FaPlus /> Add Partner
            </button>
          </PermissionGuard>
        </div>
      </CompactActionBar>

      {loading ? (
        <div style={{ background: '#fff', padding: '60px', textAlign: 'center' }}>
          <div style={{fontSize: '16px', color: '#6b7280'}}>Loading partners...</div>
        </div>
      ) : (
        <div style={{ background: '#fff', margin: 0, padding: 0 }}>
          <table className="standard-table" style={{ margin: 0 }}>
            <thead>
              <tr>
                <th>Sr. No</th>
                <th>Partner Name</th>
              <th>Projects & Percentages</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {partners.map((partner, index) => (
              <tr key={partner.id}>
                <td style={{ fontWeight: "600", color: "#4f46e5" }}>
                  {partners.length - index}
                </td>
                <td>
                  <div className="partner-name">
                    <FaHandshake className="partner-icon" />
                    {partner.name}
                  </div>
                </td>
                <td>
                  <div className="partner-projects">
                    {getPartnerProjectsDisplay(partner)}
                  </div>
                </td>
                <td>
                  <div className="partner-actions">
                    <PermissionGuard permission="partner.edit">
                      <button 
                        className="btn-icon assign"
                        onClick={() => handleAssignProject(partner)}
                        title="Assign to Project"
                        style={{ backgroundColor: '#28a745', color: 'white' }}
                      >
                        <FaProjectDiagram />
                      </button>
                    </PermissionGuard>
                    <PermissionGuard permission="partner.edit">
                      <button 
                        className="btn-icon edit"
                        onClick={() => handleEdit(partner)}
                        title="Edit Partner"
                      >
                        <FaEdit />
                      </button>
                    </PermissionGuard>
                    <PermissionGuard permission="partner.delete">
                      <button 
                        className="btn-icon delete"
                        onClick={() => handleDelete(partner)}
                        title="Delete Partner"
                      >
                        <FaTrash />
                      </button>
                    </PermissionGuard>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {partners.length === 0 && !loading && (
        <div className="empty-state">
          <FaHandshake className="empty-icon" />
          <h3>No Partners Found</h3>
          <p>Start by adding your first partner</p>
          <PermissionGuard permission="partner.create">
            <button 
              className="add-partner-btn"
              onClick={() => setShowModal(true)}
            >
              <FaPlus /> Add Partner
            </button>
          </PermissionGuard>
        </div>
      )}
      </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="pagination">
          <button
            className="page-btn"
            disabled={currentPage === 1}
            onClick={() => setCurrentPage(currentPage - 1)}
          >
            Previous
          </button>
          
          <div className="page-numbers">
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              const startPage = Math.max(1, currentPage - 2);
              const page = Math.min(startPage + i, totalPages);
              
              if (page > totalPages) return null;
              
              return (
                <button
                  key={page}
                  className={`page-btn ${currentPage === page ? 'active' : ''}`}
                  onClick={() => setCurrentPage(page)}
                >
                  {page}
                </button>
              );
            }).filter(Boolean)}
          </div>
          
          <button
            className="page-btn"
            disabled={currentPage === totalPages}
            onClick={() => setCurrentPage(currentPage + 1)}
          >
            Next
          </button>
        </div>
      )}

      {/* Add/Edit Partner Modal */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h2>{editingPartner ? 'Edit Partner' : 'Add New Partner'}</h2>
              <button 
                className="close-btn"
                onClick={() => {
                  setShowModal(false);
                  setEditingPartner(null);
                  resetForm();
                }}
              >
                ×
              </button>
            </div>
            
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="name">Partner Name *</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="Enter partner name"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="projectId">Project {editingPartner ? '(Change Assignment)' : '(Optional)'}</label>
                <select
                  id="projectId"
                  name="projectId"
                  value={formData.projectId}
                  onChange={handleInputChange}
                >
                  <option value="">Select Project</option>
                  {projects.map(project => (
                    <option key={project.id} value={project.id}>
                      {project.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="percentage">Percentage {formData.projectId ? '*' : ''}</label>
                <input
                  type="number"
                  id="percentage"
                  name="percentage"
                  value={formData.percentage}
                  onChange={handleInputChange}
                  onWheel={(e) => e.currentTarget.blur()}
                  placeholder="Enter percentage (0-100)"
                  min="0"
                  max="100"
                  step="0.01"
                  required={!!formData.projectId}
                />
              </div>

              <div className="modal-actions">
                <button 
                  type="button" 
                  className="cancel-btn"
                  onClick={() => {
                    setShowModal(false);
                    setEditingPartner(null);
                    resetForm();
                  }}
                >
                  Cancel
                </button>
                <button type="submit" className="submit-btn">
                  {editingPartner ? 'Update Partner' : 'Add Partner'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showDeleteDialog}
        title="Delete Partner"
        message={`Are you sure you want to delete "${partnerToDelete?.name}"? This action cannot be undone.`}
        onConfirm={confirmDelete}
        onCancel={() => {
          setShowDeleteDialog(false);
          setPartnerToDelete(null);
        }}
      />
    </div>
  );
};

export default Partners;
